// =====================================================
// Shared UI primitives: Toast, Modal, IconButton,
// Theme Provider, Command Palette, AI Floating FAB.
// All globally available via <UIRoot/>.
//
// Phase-14.5 C5: dropped @ts-nocheck. UseThemeReturn / UseToastReturn /
// ToastMessage / ConfirmActionOptions are the typed surface other
// chrome files (shared.tsx, pages) import. Window.toast and Window.
// confirmAction are declared here (single source of truth) so any
// page that calls them through ui.tsx's global setup gets typed
// autocomplete.
// =====================================================
import React from "react";
import { Icon } from "./icons";
// Phase-16 R5': use the typed Button primitive in the modal footer.
// Direct import (not via `./ui` barrel) to avoid the ui→ui-shell→ui
// circular dependency.
import { Button } from "./ui/Button";
// S-21 — the ⌘K registry lives in a pure data module so the nav-coherence
// vitest cross-checks palette routes against ROUTE_PATHS.
import { COMMANDS, type Command } from "./router/commands";
// S-19 — the global assistant FAB is a REAL, context-aware mini-chat now, but it
// is LAZY-loaded so its data-layer imports (endpoints/auth) stay OUT of the eager
// main bundle (perf-budget guard). See ./AIFloatingFAB.
const AIFloatingFAB = React.lazy(() => import("./AIFloatingFAB"));

// ---------- Theme ----------
export type Theme = "dark" | "light";

export interface UseThemeReturn {
  theme: Theme;
  setTheme: (t: Theme) => void;
}

const ThemeContext = React.createContext<UseThemeReturn>({
  theme: "dark",
  setTheme: () => {
    // overridden by ThemeProvider; default no-op for safety
  },
});

export const useTheme = (): UseThemeReturn => React.useContext(ThemeContext);

// ---------- Toast ----------
export type ToastKind = "info" | "success" | "warn" | "danger";

export interface ToastMessage {
  msg: string;
  title?: string;
  kind?: ToastKind;
  /** Milliseconds until auto-dismiss. Default 3200. */
  ttl?: number;
}

export type ToastInput = string | ToastMessage;

interface ToastItem extends ToastMessage {
  id: string;
}

export interface UseToastReturn {
  push: (t: ToastInput) => void;
}

const ToastContext = React.createContext<UseToastReturn>({
  push: () => {
    // overridden by UIRoot
  },
});

export const useToast = (): UseToastReturn => React.useContext(ToastContext);

// ---------- Confirm modal ----------
export interface ConfirmActionOptions {
  title?: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface ModalState extends ConfirmActionOptions {
  onConfirm: () => void;
  onCancel: () => void;
}

// ---------- Globals exposed by <UIRoot> ----------
declare global {
  interface Window {
    /** Show a toast — pass a string for a quick info toast, or a full ToastMessage. */
    toast?: (input: ToastInput, opts?: Partial<ToastMessage>) => void;
    /** Show a confirm modal; resolves true if confirmed. */
    confirmAction?: (opts: ConfirmActionOptions) => Promise<boolean>;
    /** Open the Ctrl/Cmd+K command palette. */
    openCommandPalette?: () => void;
  }
}

// ---------- IconButton ----------
export interface IconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onClick" | "className"> {
  icon: string;
  label: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  variant?: "ghost" | "primary" | "outline" | "danger";
  size?: number;
  className?: string;
}

export const IconButton = ({
  icon,
  label,
  onClick,
  variant = "ghost",
  size = 14,
  className = "",
  ...rest
}: IconButtonProps): React.ReactElement => (
  <button
    type="button"
    className={`btn btn-${variant} icon-btn ${className}`}
    onClick={onClick}
    aria-label={label}
    title={label}
    {...rest}
  >
    <Icon name={icon} size={size} />
  </button>
);

export const stubAction = (msg?: string): (() => void) => () =>
  window.toast?.(msg || "این عملیات در نسخه‌ی نمایشی فعال نیست.");

// ---------- ThemeProvider ----------
export interface ThemeProviderProps {
  children: React.ReactNode;
}

// Dark mode is DISABLED for now (owner request, D-2026-06-13). The provider is
// pinned to "light" and every persisted/system preference is ignored, so the
// app always renders the white + navy palette. `setTheme` is intentionally a
// no-op — the visible toggles were removed, but any lingering caller (command
// palette, deep links) simply does nothing instead of bringing dark back.
// Re-enable: restore readPersistedTheme + a real setTheme below.
export const ThemeProvider = ({ children }: ThemeProviderProps): React.ReactElement => {
  React.useEffect(() => {
    document.documentElement.setAttribute("data-theme", "light");
  }, []);
  const value = React.useMemo<UseThemeReturn>(
    () => ({ theme: "light" as Theme, setTheme: () => {} }),
    [],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// ---------- Command Palette ----------
// (S-21 — the COMMANDS registry moved to router/commands.ts, imported above.)

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  onNavigate: (route: string) => void;
  setTheme: (t: Theme) => void;
}

const CommandPalette = ({
  open,
  onClose,
  onNavigate,
  setTheme,
}: CommandPaletteProps): React.ReactElement | null => {
  const [q, setQ] = React.useState("");
  const [sel, setSel] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const filtered = React.useMemo<Command[]>(() => {
    if (!q.trim()) return COMMANDS.slice(0, 12);
    const lower = q.toLowerCase();
    return COMMANDS.filter(
      (c) => c.label.toLowerCase().includes(lower) || c.id.toLowerCase().includes(lower)
    ).slice(0, 20);
  }, [q]);

  React.useEffect(() => {
    if (open) {
      setQ("");
      setSel(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  React.useEffect(() => {
    setSel(0);
  }, [q]);

  const exec = (c: Command): void => {
    onClose();
    if (c.action === "theme-light") setTheme("light");
    else if (c.action === "theme-dark") setTheme("dark");
    else if (c.route) onNavigate(c.route);
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSel((s) => Math.min(s + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSel((s) => Math.max(s - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[sel]) exec(filtered[sel]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="cmdk-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cmdk-panel" role="dialog" aria-label="پالت دستورات" aria-modal="true">
        <div className="cmdk-input-wrap">
          <Icon name="search" size={16} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="جستجوی صفحه، دستور یا تنظیم… (مثلا: کلاس، تم، گواهی)"
            aria-label="جستجوی دستورات"
            spellCheck={false}
            autoComplete="off"
          />
          <kbd className="cmdk-kbd">ESC</kbd>
        </div>
        <div className="cmdk-list" role="listbox">
          {filtered.length === 0 && (
            <div className="cmdk-empty">نتیجه‌ای یافت نشد.</div>
          )}
          {filtered.map((c, i) => (
            <button
              key={c.id}
              className={"cmdk-item " + (i === sel ? "selected" : "")}
              onClick={() => exec(c)}
              onMouseEnter={() => setSel(i)}
              role="option"
              aria-selected={i === sel}
            >
              <span className="cmdk-icon"><Icon name={c.icon} size={14} /></span>
              <span className="cmdk-label">{c.label}</span>
              {c.hint && <span className="cmdk-hint">{c.hint}</span>}
            </button>
          ))}
        </div>
        <div className="cmdk-footer">
          <span><kbd>↑↓</kbd> ناوبری</span>
          <span><kbd>Enter</kbd> انتخاب</span>
          <span><kbd>Esc</kbd> بستن</span>
        </div>
      </div>
    </div>
  );
};

// ---------- UIRoot ----------
export interface UIRootProps {
  children: React.ReactNode;
  onNavigate?: (route: string) => void;
}

export const UIRoot = ({ children, onNavigate }: UIRootProps): React.ReactElement => {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);
  const [modal, setModal] = React.useState<ModalState | null>(null);
  const [cmdOpen, setCmdOpen] = React.useState(false);
  const { setTheme } = useTheme();

  const push = React.useCallback((input: ToastInput): void => {
    const id = Math.random().toString(36).slice(2);
    const item: ToastMessage =
      typeof input === "object" && input !== null && !Array.isArray(input)
        ? input
        : { msg: String(input) };
    setToasts((arr) => [...arr, { id, kind: "info", ...item }]);
    const ttl = item.ttl ?? 3200;
    setTimeout(() => setToasts((arr) => arr.filter((x) => x.id !== id)), ttl);
  }, []);
  const dismiss = (id: string): void =>
    setToasts((arr) => arr.filter((x) => x.id !== id));

  // global helpers — exposed on window for non-React call sites
  React.useEffect(() => {
    window.toast = (m, opts = {}) => {
      if (typeof m === "object" && m !== null && !Array.isArray(m)) push({ ...m, ...opts });
      else push({ msg: String(m), ...opts });
    };
    window.confirmAction = (opts: ConfirmActionOptions): Promise<boolean> =>
      new Promise((resolve) => {
        setModal({
          ...opts,
          onConfirm: () => { setModal(null); resolve(true); },
          onCancel: () => { setModal(null); resolve(false); },
        });
      });
    window.openCommandPalette = () => setCmdOpen(true);
  }, [push]);

  // Cmd+K / Ctrl+K to open palette
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const toastValue = React.useMemo<UseToastReturn>(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={toastValue}>
      {children}
      <div className="toast-stack" aria-live="polite" aria-atomic="true">
        {toasts.map((t) => (
          <div key={t.id} className={"toast toast-" + (t.kind || "info")} role="status">
            <div className="toast-icon" aria-hidden="true">
              <Icon name={t.kind === "success" ? "check" : t.kind === "danger" ? "shield" : t.kind === "warn" ? "bell" : "sparkle"} size={14} />
            </div>
            <div className="toast-body">
              {t.title && <div className="toast-title">{t.title}</div>}
              <div className="toast-msg">{t.msg}</div>
            </div>
            <button className="toast-close" onClick={() => dismiss(t.id)} aria-label="بستن">
              <Icon name="end" size={12} />
            </button>
          </div>
        ))}
      </div>

      {modal && (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          onClick={(e) => { if (e.target === e.currentTarget) modal.onCancel?.(); }}
        >
          <div className="modal-card">
            <h3 id="modal-title" className="modal-title">{modal.title || "تأیید عملیات"}</h3>
            {modal.body && <p className="modal-body">{modal.body}</p>}
            <div className="modal-actions">
              <Button variant="ghost" onClick={modal.onCancel}>{modal.cancelLabel || "انصراف"}</Button>
              <Button variant="primary" onClick={modal.onConfirm} autoFocus>{modal.confirmLabel || "تأیید"}</Button>
            </div>
          </div>
        </div>
      )}

      <CommandPalette
        open={cmdOpen}
        onClose={() => setCmdOpen(false)}
        onNavigate={(id) => onNavigate?.(id)}
        setTheme={setTheme}
      />

      {/* S-19 — lazy so its data-layer imports stay off the eager main bundle;
          the floating helper may pop in a beat after first paint (fine). */}
      <React.Suspense fallback={null}>
        <AIFloatingFAB onNavigate={(id) => onNavigate?.(id)} />
      </React.Suspense>
    </ToastContext.Provider>
  );
};
